import { useEffect, useLayoutEffect, useMemo, useRef, useState, useContext } from "react";
import { TranscriptInSheet, TranscriptOpener, useSheetMeta } from "../lib/transcriptSheet";
import { Maximize2 } from "react-feather";
import type { ReactNode, RefObject } from "react";
import { useDelegateTimeline } from "../lib/useDelegateTimeline";
import type { DelegateArchive, DelegateTimelineEntry } from "../lib/types";
import type { TimelineScope } from "../lib/delegateTimeline";
import DelegateTimelineImage from "./DelegateTimelineImage";
import { openedCards } from "../lib/openedCards";

type Update = DelegateTimelineEntry;

/** Small link-only renderer: escaped labels/text, not HTML or general Markdown. */
export function PublicUpdateText({text}:{text:string}) {
  return <>{text.split(/(\[[^\]\n]+\]\([^\s()]*\)|https?:\/\/[^\s<>]+)/gi).map((part,index) => {
    const labelled = /^\[([^\]\n]+)\]\(([^\s()]*)\)$/.exec(part);
    if (!labelled && !/^https?:\/\//i.test(part)) return part;
    const clean = labelled ? labelled[2]! : part.replace(/[.,;!?)]+$/, "");
    try {
      const url = new URL(clean);
      if (!["https:","http:"].includes(url.protocol) || url.username || url.password) return part;
      return <span key={index}><a href={clean} target="_blank" rel="noopener noreferrer">{labelled ? labelled[1] : clean}</a>{labelled ? "" : part.slice(clean.length)}</span>;
    } catch { return part; }
  })}</>;
}

function UpdateRow({entry,scope}:{entry:Update;scope:TimelineScope}) {
  return <li className={entry.type === "image" && entry.imageStatus !== "expired" ? "is-image" : undefined}>
      <time dateTime={entry.updatedAt} title={new Date(entry.updatedAt).toLocaleString()}>{new Date(entry.updatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</time>
      <div className="delegateProgress__body">{entry.text && <p><PublicUpdateText text={entry.text} /></p>}
        {entry.type === "image" && entry.image && <DelegateTimelineImage image={entry.image} scope={scope} />}</div>
    </li>;
}

export default function DelegateUpdateList({ entries, scope }: { entries: Update[]; scope: TimelineScope }) {
  return <ol className="delegateProgress__updates">
    {entries.map((entry, index) => <UpdateRow entry={entry} scope={scope} key={entry.id ?? entry.updatedAt + ":" + index} />)}
  </ol>;
}

type DisplayName = "Astra" | "Claude" | "Opus" | "Agent";
function TimelineFrame({displayName,active,phase,children,cardRef,phaseSpinner=true,empty=false,openKey}:{
  displayName:DisplayName;active?:boolean;phase?:string;children:ReactNode;cardRef?:RefObject<HTMLDivElement|null>;phaseSpinner?:boolean;empty?:boolean;openKey?:string;
}) {
  // In the transcript pop-up every card is open and has no toggle of its own.
  const inSheet = useContext(TranscriptInSheet);
  const openTranscript = useContext(TranscriptOpener);
  const [collapsedState,setCollapsed] = useState(() => !(openKey && openedCards.has(openKey)));
  const collapsed = inSheet ? false : collapsedState;
  // A frame kept across a new card key shows that card's own open state, never the last one's.
  const [shownKey,setShownKey] = useState(openKey);
  if (shownKey !== openKey) {
    setShownKey(openKey);
    setCollapsed(!(openKey && openedCards.has(openKey)));
  }
  // Closed content is truly hidden (not just 0 px tall) once its close animation ends.
  const [bodyHidden, setBodyHidden] = useState(collapsed);
  const [hiddenFor, setHiddenFor] = useState(collapsed);
  if (hiddenFor !== collapsed) {
    setHiddenFor(collapsed);
    if (!collapsed) setBodyHidden(false);
  }
  useEffect(() => {
    if (!collapsed || bodyHidden) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const timer = window.setTimeout(() => setBodyHidden(true), reduce ? 0 : 340);
    return () => window.clearTimeout(timer);
  }, [collapsed, bodyHidden]);
  const ownCard = useRef<HTMLDivElement>(null);
  const frame = cardRef ?? ownCard;
  const opening = useRef(false);
  useLayoutEffect(() => {
    if (inSheet || !opening.current || collapsed) return;
    opening.current = false;
    const card = frame.current, scroller = card?.closest(".chat__scroll");
    // Only when the heading is out of view: a card opened from where it stands just grows
    // downward (the chat holds its heading still), with no second scroll fighting it.
    if (card && scroller && card.getBoundingClientRect().top < scroller.getBoundingClientRect().top) {
      // Glide to the heading instead of jumping (instant when reduced motion is on).
      const top = scroller.scrollTop + card.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 12;
      const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (typeof scroller.scrollTo === "function") scroller.scrollTo({ top, behavior: smooth ? "smooth" : "auto" });
      else scroller.scrollTop = top;
    }
  }, [collapsed, frame]);
  const heading = displayName === "Agent" ? "Reputifly Updates" : "Reputifly " + displayName + " Updates";
  // In the pop-up the sheet header says "Transcript · Opus · Updates saved" instead (30 Sep).
  const reportMeta = useSheetMeta((s) => s.report);
  useLayoutEffect(() => {
    if (inSheet) reportMeta({ name: displayName === "Agent" ? "" : displayName, phase: phase ?? "", active: !!active });
  }, [inSheet, displayName, phase, active, reportMeta]);
  return <div className={"delegateProgress__card" + (collapsed ? " is-collapsed" : "") + (empty ? " is-empty" : "") + (active ? " is-active" : "")} ref={frame}>
    {!inSheet && <header className="delegateProgress__header">
      <h3 className="delegateProgress__heading" aria-label={heading}><button type="button" className="delegateProgress__toggle" aria-expanded={!collapsed}
        onClick={() => {
          // Bubble a reading intent before changing height. The parent suspends
          // live bottom-follow so opening starts at this heading, even on mobile.
          frame.current?.dispatchEvent(new CustomEvent("transcript-reading", { bubbles: true, detail: { card: frame.current } }));
          opening.current = collapsed;
          if (openKey) { if (collapsed) openedCards.add(openKey); else openedCards.delete(openKey); }
          setCollapsed(value => !value);
        }} aria-label={(collapsed ? "Expand " : "Collapse ") + displayName + " updates"}>
        {heading}<span aria-hidden="true" className={"delegateProgress__chevron" + (collapsed ? " is-collapsed" : "")} />
      </button></h3>
      {phase && <span className={"delegateProgress__phase" + (active ? " is-active" : "")}>
        {active && phaseSpinner && <span className="delegateProgress__spinner" aria-hidden="true" />}{phase}</span>}
      {/* Always present (never appears late), so the header never changes height. */}
      {openTranscript && !inSheet && (
        <button type="button" className="delegateProgress__open" aria-label="Open the transcript" onClick={openTranscript}>
          <Maximize2 width={14} height={14} strokeWidth={1.75} aria-hidden="true" />
        </button>
      )}
    </header>}
    {/* Opens and closes smoothly (grid rows 0fr ↔ 1fr) instead of dropping in at once
        (30 Sep). Closed content is inert: not focusable, not read by screen readers. */}
    <div className={"delegateProgress__drawer" + (collapsed ? "" : " is-open")} hidden={collapsed && bodyHidden} aria-hidden={collapsed} inert={collapsed || undefined}>
      <div className="delegateProgress__drawerInner">{children}</div>
    </div>
  </div>;
}

/** Already-loaded contiguous provider segment; each image keeps its original archive scope. */
export function DelegateTimelineSegment({segmentKey,items,displayName,active=false,phase}:{
  segmentKey?:string;items:{key:string;entry:Update;scope:TimelineScope}[];displayName:DisplayName;active?:boolean;phase?:string;
}) {
  return <TimelineFrame displayName={displayName} active={active} phase={phase} phaseSpinner={false} empty={items.length===0} openKey={segmentKey}>
    <div className="delegateProgress__feed" role="log" aria-label={"Public "+displayName+" updates"} aria-live={active?"polite":"off"}>
      <ol className="delegateProgress__updates">{items.map(item => <UpdateRow key={item.key} entry={item.entry} scope={item.scope} />)}</ol>
    </div>
  </TimelineFrame>;
}

type TimelineProps = { entries: Update[]; companyId: string; runId: string; requestMessageId: string;
  active?: boolean; phase?: string; archive?: DelegateArchive; provider?: "astra" | "claude"; displayName?: "Astra" | "Claude" | "Opus" };

export function DelegateTimelineCard({ entries, companyId, runId, requestMessageId, active = false, phase, archive, provider = "astra", displayName = "Astra" }: TimelineProps) {
  const scope = useMemo(() => ({ companyId, runId, requestMessageId, provider }), [companyId, runId, requestMessageId, provider]);
  const card = useRef<HTMLDivElement>(null);
  const anchor = useRef<{element:Element;top:number;scroller:Element}|null>(null);
  const [visible,setVisible] = useState(() => active || typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (active || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(changes => {
      if (changes.some(entry => entry.isIntersecting)) {setVisible(true);observer.disconnect();}
    },{rootMargin:"600px"});
    if (card.current) observer.observe(card.current);
    return () => observer.disconnect();
  }, [active]);
  const timeline = useDelegateTimeline(scope, entries, active || visible ? archive : undefined);
  useLayoutEffect(() => {
    const saved = anchor.current;
    if (saved && !timeline.loading) {
      saved.scroller.scrollTop += saved.element.getBoundingClientRect().top - saved.top;
      anchor.current = null;
    }
  }, [timeline.entries,timeline.loading]);
  function loadOlder() {
    const element = card.current?.querySelector("li");
    const scroller = card.current?.closest(".chat__scroll");
    if (element && scroller) anchor.current = {element,scroller,top:element.getBoundingClientRect().top};
    void timeline.loadOlder();
  }
  return <TimelineFrame displayName={displayName} active={active} phase={phase} cardRef={card} openKey={requestMessageId + ":" + runId}>
    {timeline.hasOlder && <button type="button" className="delegateProgress__load" disabled={timeline.loading}
      onClick={loadOlder}>{timeline.loading ? "Loading earlier updates…" : "Load earlier updates"}</button>}
    {timeline.error && <p className="delegateProgress__archiveError">Earlier updates are unavailable.
      <button type="button" onClick={timeline.retry}>Retry</button></p>}
    <div className="delegateProgress__feed" role="log" aria-label={"Public " + displayName + " updates"} aria-live={active ? "polite" : "off"}>
      {timeline.entries.length ? <DelegateUpdateList entries={timeline.entries} scope={scope} /> : <p className="delegateProgress__empty">
        {phase === "Queued" ? "Waiting for execution to start." : "Public updates will appear here."}
      </p>}
    </div>
  </TimelineFrame>;
}

export function SavedDelegateUpdates(props: TimelineProps) {
  return <div className="delegateSavedUpdates"><DelegateTimelineCard {...props} /></div>;
}
